import React, { useState } from 'react';
import {
  LinkAsButton, Heading, Icon, Listbox, Grid, CheckboxGroup,
} from '@printdeal/ui-library';
import { FormattedMessage } from 'react-intl';
import { useInView } from 'react-intersection-observer';
import { ReviewStars } from '../../../review-stars/ReviewStars';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import type { Review } from '../../../../helpers/reviewHelper';

const AMOUNT_OF_REVIEWS = 2;

type Sort = 'newest' | 'oldest' | 'highest' | 'lowest';

interface Props {
  productReviews: {
    ratings: Record<string, number>;
    average_rating: number;
    review_count: number;
    results_count: number;
    reviews: Review[];
  }
}

const sortReviews = (reviews: Review[], sort: Sort) => {
  switch (sort) {
    case 'newest':
      return reviews.sort((a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime());
    case 'oldest':
      return reviews.sort((a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime());
    case 'highest':
      return reviews.sort((a, b) => b.rating - a.rating);
    case 'lowest':
      return reviews.sort((a, b) => a.rating - b.rating);
    default:
      return reviews;
  }
};

/**
 * @param productKey
 * @param productReviews
 * @constructor
 *
 * @deprecated - This component is deprecated and will be removed in the future.
 * We recommend using the ReviewsIO component instead.
 */
const StaticReviews = ({ productReviews }: Props) => {
  const [amount, setAmount] = useState(AMOUNT_OF_REVIEWS);
  const [filterStars, setFilterStars] = useState<number[]>([]);
  const [sort, setSort] = useState<Sort>('newest');

  const reviews = filterStars.length
    ? productReviews.reviews.filter((review) => filterStars.includes(review.rating))
    : productReviews.reviews;

  const sortedReviews = sortReviews(reviews, sort);

  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => inView && dispatchDataLayerEntry('productDetailReviews', 'display'),
  });

  const handleChange = (rating: number) => {
    if (filterStars.includes(rating)) {
      setFilterStars(filterStars.filter((star) => star !== rating));
    } else {
      setFilterStars([...filterStars, rating]);
    }
  };

  return (
    <>
      <Heading as="h2" variant="display" className="pd-mb-7">
        <FormattedMessage id="productDetail.reviews.title" defaultMessage="Reviews" />
      </Heading>
      <div className="pd-mb-7 pd-flex pd-items-center pd-gap-2">
        <ReviewStars score={productReviews.average_rating} />
        <FormattedMessage
          id="productDetail.reviews.total"
          defaultMessage="{total} Reviews"
          values={{ total: productReviews.results_count }}
        />
      </div>
      <CheckboxGroup id="star-filter" name="star-filter">
        {Object.keys(productReviews.ratings)
          .sort((ratingA, ratingB) => Number(ratingB) - Number(ratingA))
          .map((rating) => (
            <CheckboxGroup.Field
              key={rating}
              variant="secondary"
              id={rating}
              label={(
                <FormattedMessage
                  id="productDetail.reviews.stars"
                  defaultMessage="{rating} {rating, plural, one {star} other {stars}} ({count})"
                  values={{
                    rating,
                    count: productReviews.ratings[rating],
                  }}
                />
              )}
              onChange={() => handleChange(Number(rating))}
            />
          ))}
      </CheckboxGroup>
      <div className="pd-my-7 pd-flex pd-justify-start">
        <Listbox value={sort} onChange={(value) => setSort(value as Sort)}>
          <Listbox.Option value="newest">
            <FormattedMessage id="productDetail.reviews.filter.newest" defaultMessage="Newest reviews" />
          </Listbox.Option>
          <Listbox.Option value="oldest">
            <FormattedMessage id="productDetail.reviews.filter.oldest" defaultMessage="Oldest reviews" />
          </Listbox.Option>
          <Listbox.Option value="highest">
            <FormattedMessage id="productDetail.reviews.filter.highest" defaultMessage="Highest reviews" />
          </Listbox.Option>
          <Listbox.Option value="lowest">
            <FormattedMessage id="productDetail.reviews.filter.lowest" defaultMessage="Lowest reviews" />
          </Listbox.Option>
        </Listbox>
      </div>
      {sortedReviews.slice(0, amount).map((review) => (
        <Grid key={review.id} ref={ref} className="pd-mb-6">
          <Grid.Item className="pd-mb-3 pd-text-md pd-font-bold">{review.title}</Grid.Item>
          <Grid.Item className="pd-mb-2 pd-flex pd-items-center">
            <ReviewStars className="pd-mr-2" score={review.rating * 2} />
            {review.rating}/5
          </Grid.Item>
          <Grid.Item className="pd-mb-2 pd-flex pd-items-center">
            <Icon icon="check" className="pd-mr-2 pd-text-success-600" size="md" />
            <FormattedMessage id="productDetail.reviews.verified" defaultMessage="Verified customer" />
          </Grid.Item>
          <Grid.Item className="pd-mb-2">
            {review.author.name && `${review.author.name} / `} {review.author.location}
          </Grid.Item>
          <Grid.Item>
            {review.comments}
          </Grid.Item>
        </Grid>
      ))}
      {amount < reviews.length && (
        <LinkAsButton
          type="button"
          onClick={() => setAmount(amount + 5)}
          className="pd-pl-1 pd-text-md pd-font-semibold pd-text-black"
          iconBefore={(<Icon icon="double-chevron-down" className="pd-text-dim-500" />)}
        >
          <FormattedMessage id="productDetail.reviews.more" defaultMessage="Show more reviews" />
        </LinkAsButton>
      )}
    </>
  );
};

export default StaticReviews;
